import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Stack,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarViewDayIcon from "@mui/icons-material/CalendarViewDay";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PaymentsIcon from "@mui/icons-material/Payments";
import TireRepair from "@mui/icons-material/TireRepair";

function Reports() {
  const cards = [
    {
      title: "Daily Report",
      description: "Review one day of sales, payments, service lines, and balances.",
      to: "/user/reports/daily",
      icon: <CalendarViewDayIcon sx={{ fontSize: 28, color: "#2563eb" }} />,
    },
    {
      title: "Daily Payment History",
      description: "See who paid, how they paid, and which invoices were touched that day.",
      to: "/user/reports/daily-payments",
      icon: <PaymentsIcon sx={{ fontSize: 28, color: "#059669" }} />,
    },
    {
      title: "Bulk Payment History",
      description: "Review and reprint grouped bulk payment receipts.",
      to: "/user/reports/bulk-payments",
      icon: <PaymentsIcon sx={{ fontSize: 28, color: "#0f766e" }} />,
    },
    {
      title: "Break DownReport",
      description: "Tires, oil changes, inspections, and services by date range.",
      to: "/user/reports/weekly",
      icon: <DateRangeIcon sx={{ fontSize: 28, color: "#d97706" }} />,
    },
    {
      title: "Monthly Report",
      description: "Track daily business, collections, unpaid balances, and taxes for the month.",
      to: "/user/reports/monthly",
      icon: <CalendarMonthIcon sx={{ fontSize: 28, color: "#0f766e" }} />,
    },
    {
      title: "Parts Report",
      description: "Review special-order parts purchased, sold, returned, and still open by vendor.",
      to: "/user/reports/parts",
      icon: <Inventory2Icon sx={{ fontSize: 28, color: "#7c3aed" }} />,
    },
    {
      title: "Tire Analysis Report",
      description: "See tire sizes sold by new and used condition with brand breakdowns.",
      to: "/user/reports/tire-analysis",
      icon: <TireRepair sx={{ fontSize: 28, color: "#2563eb" }} />,
    },
  ];
  const reportSections =
    cards.length >= 9
      ? cards
      : [
          ...cards,
          ...Array.from({ length: 9 - cards.length }, (_, index) => ({
            title: `empty-${index}`,
            empty: true,
          })),
        ];

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 3,
            background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 55%, #0f766e 100%)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: 300,
              height: 300,
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            <MuiLink
              component={Link}
              to="/user/dashboard"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Dashboard
            </MuiLink>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Reports
            </Typography>
          </Breadcrumbs>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ zIndex: 1, position: "relative" }}
          >
            <AssessmentIcon sx={{ fontSize: 38, color: "white" }} />
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                Reports
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}
              >
                Open daily, weekly, and monthly reporting from one place.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(3, minmax(0, 1fr))",
            },
            gap: 3,
          }}
        >
          {reportSections.map((card) =>
            card.empty ? (
              <Box
                key={card.title}
                aria-hidden="true"
                sx={{
                  minHeight: 180,
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "rgba(148, 163, 184, 0.35)",
                  backgroundColor: "rgba(255,255,255,0.45)",
                }}
              />
            ) : (
              <Box
                key={card.title}
                component={Link}
                to={card.to}
                sx={{
                  textDecoration: "none",
                  minHeight: 180,
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>{card.icon}</Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#1f2937", mb: 1 }}
                >
                  {card.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#667085" }}>
                  {card.description}
                </Typography>
              </Box>
            ),
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default Reports;
